import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useMessageBox } from './MessageBoxContainer';
import { getCandidateName } from '../constants/candidatesData';

const AdvancedReports = ({ token, backendUrl }) => {
  const messageBox = useMessageBox();
  const [ranking, setRanking] = useState(null);
  const [participation, setParticipation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateVoters, setCandidateVoters] = useState([]);
  const rankingRef = useRef(null);

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      // Load ranking
      const rankingRes = await fetch(`${backendUrl}/api/admin/reports/ranking`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (rankingRes.ok) {
        const rankingData = await rankingRes.json();
        setRanking(rankingData.ranking);
      }

      // Load participation
      const participationRes = await fetch(`${backendUrl}/api/admin/reports/participation`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (participationRes.ok) {
        const participationData = await participationRes.json();
        setParticipation(participationData);
      }
    } catch (error) {
      messageBox.error('<strong>خطا در بارگذاری گزارشات</strong>\n\nمتأسفانه گزارشات بارگذاری نشد\n\n• لطفاً اتصال اینترنت خود را بررسی کنید');
    } finally {
      setLoading(false);
    }
  };

  const loadCandidateVoters = async (candidateId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/candidate/${candidateId}/voters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidateVoters(data.voters);
        setSelectedCandidate(candidateId);
      }
    } catch (error) {
      messageBox.error('<strong>خطا در بارگذاری اطلاعات نامزد</strong>\n\nمتأسفانه اطلاعات رای‌دهندگان بارگذاری نشد');
    }
  };

  const exportRankingToPDF = async () => {
    if (!rankingRef.current) return;

    const loadingId = messageBox.loading('در حال تولید فایل PDF...');
    
    try {
      const canvas = await html2canvas(rankingRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add header
      pdf.setFontSize(16);
      pdf.text('گزارش رتبه‌بندی', pdfWidth / 2, 15, { align: 'center' });
      pdf.setFontSize(12);
      pdf.text('سامانه انجمن دانشگاه شمال آمل', pdfWidth / 2, 22, { align: 'center' });

      let position = 30;
      if (imgHeight > pdfHeight - 40) {
        // Multiple pages
        let heightLeft = imgHeight;
        let pagePosition = position;

        pdf.addImage(imgData, 'PNG', 10, pagePosition, imgWidth, imgHeight);
        heightLeft -= (pdfHeight - pagePosition);

        while (heightLeft > 0) {
          pagePosition = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 10, pagePosition, imgWidth, imgHeight);
          heightLeft -= pdfHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      }

      pdf.save(`ranking_report_${new Date().toISOString().split('T')[0]}.pdf`);
      messageBox.hideMessage(loadingId);
      messageBox.success('<strong>دانلود موفقیت‌آمیز بود!</strong>\n\nفایل PDF با موفقیت ذخیره شد', 4000);
    } catch (error) {
      messageBox.hideMessage(loadingId);
      messageBox.error('<strong>خطا در تولید PDF</strong>\n\nمتأسفانه فایل PDF تولید نشد\n\n• لطفاً دوباره تلاش کنید');
    }
  };

  const getFacultyPersianName = (facultyCode) => {
    const names = {
      'engineering': 'دانشکده فنی و مهندسی',
      'humanities': 'دانشکده علوم انسانی',
      'sports': 'دانشکده علوم ورزشی'
    };
    return names[facultyCode] || facultyCode;
  };

  const getMajorPersianName = (majorCode) => {
    const names = {
      'computer': 'مهندسی کامپیوتر',
      'chemical': 'مهندسی شیمی',
      'electrical': 'مهندسی برق',
      'civil': 'مهندسی عمران',
      'mechanical': 'مهندسی مکانیک',
      'industrial': 'مهندسی صنایع',
      'architecture': 'مهندسی معماری',
      'management': 'مدیریت',
      'psychology': 'روانشناسی',
      'accounting': 'حسابداری',
      'law': 'حقوق',
      'physical': 'تربیت بدنی و علوم ورزشی'
    };
    return names[majorCode] || majorCode;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#667eea' }}></i>
        <p>در حال بارگذاری گزارشات...</p>
      </div>
    );
  }

  return (
    <div className="advanced-reports" style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '30px', color: '#1f2937', fontFamily: 'Vazirmatn' }}>
        <i className="fas fa-chart-line"></i> گزارشات پیشرفته
      </h2>

      {/* Participation Report */}
      {participation && (
        <div style={{ 
          backgroundColor: '#fff', 
          borderRadius: '16px', 
          padding: '24px', 
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ marginBottom: '20px', color: '#374151' }}>
            <i className="fas fa-users"></i> گزارش مشارکت
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '20px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <h4 style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>کل دانشجویان</h4>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>
                {participation.total_students.toLocaleString('fa-IR')}
              </p>
            </div>
            <div style={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              padding: '20px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <h4 style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>تعداد رای‌دهندگان</h4>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>
                {participation.total_votes.toLocaleString('fa-IR')}
              </p>
            </div>
            <div style={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              padding: '20px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <h4 style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>میزان مشارکت</h4>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>
                {participation.overall_participation_rate.toLocaleString('fa-IR')}%
              </p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={Object.entries(participation.faculty_participation).map(([faculty, data]) => ({
                name: getFacultyPersianName(faculty),
                'کل دانشجویان': data.total_students,
                'رای‌دهندگان': data.total_votes
              }))}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{ fontFamily: 'Vazirmatn', fontSize: '12px' }} />
              <YAxis style={{ fontFamily: 'Vazirmatn', fontSize: '12px' }} />
              <Tooltip contentStyle={{ fontFamily: 'Vazirmatn', direction: 'rtl' }} />
              <Legend wrapperStyle={{ fontFamily: 'Vazirmatn' }} />
              <Bar dataKey="کل دانشجویان" fill="#667eea" />
              <Bar dataKey="رای‌دهندگان" fill="#43e97b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Ranking Report */}
      {ranking && (
        <div ref={rankingRef} style={{ 
          backgroundColor: '#fff', 
          borderRadius: '16px', 
          padding: '24px', 
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#374151' }}>
              <i className="fas fa-trophy"></i> رتبه‌بندی نامزدها
            </h3>
            <button
              onClick={exportRankingToPDF}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: 'Vazirmatn',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-file-pdf"></i>
              دانلود PDF
            </button>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
            <h2 style={{ color: '#1f2937', margin: '0 0 8px 0' }}>گزارش رتبه‌بندی نامزدها</h2>
            <p style={{ color: '#6b7280', margin: 0 }}>سامانه انجمن دانشگاه شمال آمل</p>
          </div>

          {Object.entries(ranking).map(([major, data]) => (
            <div key={major} style={{ 
              marginBottom: '32px',
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: '2px solid #e5e7eb'
            }}>
              <h4 style={{ 
                marginBottom: '16px', 
                color: '#374151',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                <i className="fas fa-graduation-cap"></i> {getMajorPersianName(major)}
                <span style={{ 
                  fontSize: '14px', 
                  color: '#6b7280', 
                  fontWeight: 'normal',
                  marginRight: '8px'
                }}>
                  ({getFacultyPersianName(data.faculty)})
                </span>
              </h4>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                      <th style={{ padding: '12px', textAlign: 'center' }}>رتبه</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>شماره نامزد</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>تعداد رای</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.candidates.map((candidate) => (
                      <tr key={candidate.candidate_id} style={{ 
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: candidate.rank === 1 ? '#fef3c7' : 'white'
                      }}>
                        <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                          {candidate.rank === 1 && <i className="fas fa-crown" style={{ color: '#f59e0b', marginLeft: '4px' }}></i>}
                          {candidate.rank.toLocaleString('fa-IR')}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {getCandidateName(candidate.candidate_id)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#667eea' }}>
                          {candidate.votes.toLocaleString('fa-IR')} رای
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => loadCandidateVoters(candidate.candidate_id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <i className="fas fa-eye"></i> مشاهده رای‌دهندگان
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '16px' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={data.candidates.map(c => ({
                      name: getCandidateName(c.candidate_id),
                      رای: c.votes
                    }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" style={{ fontFamily: 'Vazirmatn', fontSize: '11px' }} />
                    <YAxis style={{ fontFamily: 'Vazirmatn', fontSize: '11px' }} />
                    <Tooltip contentStyle={{ fontFamily: 'Vazirmatn', direction: 'rtl' }} />
                    <Bar dataKey="رای" fill="#667eea" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Candidate Voters Modal */}
      {selectedCandidate && candidateVoters.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>
                رای‌دهندگان {getCandidateName(selectedCandidate)}
              </h3>
              <button
                onClick={() => {
                  setSelectedCandidate(null);
                  setCandidateVoters([]);
                }}
                style={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-times"></i> بستن
              </button>
            </div>

            <p style={{ marginBottom: '16px', color: '#6b7280' }}>
              تعداد کل: {candidateVoters.length.toLocaleString('fa-IR')} نفر
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>شماره دانشجویی</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>نام دانشجو</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>رشته</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>تاریخ رای</th>
                </tr>
              </thead>
              <tbody>
                {candidateVoters.map((voter, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>{voter.student_id}</td>
                    <td style={{ padding: '12px' }}>{voter.student_name}</td>
                    <td style={{ padding: '12px' }}>{voter.major}</td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                      {new Date(voter.voted_at).toLocaleString('fa-IR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedReports;
